import React from 'react'
import { observer } from 'mobx-react'
import Avatar from 'components/Avatar'
import Button from 'components/Button'
import { ContentLayout } from 'components/ContentLayout'
import Icon from 'components/Icon'
import {
  ModalPanel,
  ModalPanelHeader,
  ModalPanelContent,
} from 'components/ModalPanel'
import {
  StickyViewHeader,
  StickyViewHeaderExtras,
  StickyViewHeaderMain,
} from 'components/StickyViewHeader'
import { TableHost, TableScrollingContainer } from 'components/Table'
import {
  ScrollOffsetInterpolation,
  useScrollOffsetSpring,
} from 'hooks/spring-hooks'
import { TableSearchbar } from 'payouts/components/TableSearchbar'
import ApplicantManagementPanelViewModel from './ApplicantManagementPanelViewModel'
import styles from './ApplicantManagementPanel.sass'
import { Dropdown, DropdownItem, DropdownTitle } from 'components/Dropdown'
import Tooltip from 'components/Tooltip'
import ApplicantManagementTable from '../ApplicantManagementTable'

interface ApplicantManagementPanelProps {
  vm: ApplicantManagementPanelViewModel
}

export default observer(function ApplicantManagementPanel({
  vm,
}: ApplicantManagementPanelProps) {
  const scrollRef = React.useRef<HTMLDivElement | null>(null)
  const scrollOffset = useScrollOffsetSpring(scrollRef)

  return (
    <ModalPanel
      showing={vm.showing}
      onRequestClose={vm.close}
      disableCloseOnOutsideClick
    >
      <ModalPanelHeader withCloseButton closeButtonDataTest={undefined} />
      <ModalPanelContent>
        <div className={styles.root} ref={scrollRef}>
          <ApplicantManagementPanelHeader vm={vm} scrollOffset={scrollOffset} />
          <ApplicantManagementPanelContent vm={vm}>
            <ApplicantManagementTable vm={vm} />
          </ApplicantManagementPanelContent>
        </div>
      </ModalPanelContent>
    </ModalPanel>
  )
})

interface ApplicantManagementPanelHeaderProps {
  vm: ApplicantManagementPanelViewModel
  scrollOffset: ScrollOffsetInterpolation
}

const ApplicantManagementPanelHeader = observer(
  function ApplicantManagementPanelHeader({
    vm,
    scrollOffset,
  }: ApplicantManagementPanelHeaderProps) {
    const workspace = vm.workspacePosting?.workspace
    return (
      <StickyViewHeader scrollOffset={scrollOffset} noTopPadding>
        <StickyViewHeaderMain>
          <div className={styles.headerMainContainer}>
            <Avatar
              size={48}
              square
              subject={{
                displayName: workspace?.name,
                avatar: workspace?.branding.logos.icon,
              }}
            />
            <div className={styles.headerTitle}>
              <p className={styles.subtitleText}>Applicant manager</p>
              <h3 className={styles.titleText}>{workspace?.name}</h3>
            </div>
          </div>
        </StickyViewHeaderMain>
        <StickyViewHeaderExtras>
          {/* TODO: Enable when invite pro modal is set up. */}
          {/* <Button type="secondary" onClick={() => console.log('invite applicant')}>
            <Icon name="add" size={16} />
            <span className={styles.buttonText}>Invite Pro</span>
          </Button> */}
        </StickyViewHeaderExtras>
      </StickyViewHeader>
    )
  },
)

interface ApplicantManagementPanelContentProps {
  vm: ApplicantManagementPanelViewModel
  children?: React.ReactNode
}

const ApplicantManagementPanelContent = observer(
  function ApplicantManagementPanelContent({
    vm,
    children,
  }: ApplicantManagementPanelContentProps) {
    const totalApplicants = vm.workspacePosting?.applicants.length

    return (
      <div className={styles.content}>
        <div className={styles.applicantCountContainer}>
          Applicants
          <span className={styles.applicantCount}>
            ({totalApplicants ?? 0})
          </span>
        </div>
        <ContentLayout>
          <TableHost>
            <TableSearchbar
              searchText={vm.searchText}
              onSearchTextChange={(value) => vm.setSearchText(value)}
            >
              <Dropdown
                autoDismiss
                width="miniscule"
                position="bottom-end"
                button={
                  <Tooltip content={'Menu'} placement="bottom">
                    <Button
                      size="baseline"
                      type="text"
                      dataTest={`applicant-manager-bulk-actions-open-${vm.workspacePosting?.id}`}
                    >
                      <Icon color="var(--grey600)" name="more" />
                    </Button>
                  </Tooltip>
                }
              >
                <div>
                  <DropdownTitle>Bulk actions</DropdownTitle>
                  <DropdownItem
                    onClick={vm.inviteSelected}
                    disabled={
                      vm.selectedApplicants.size < 1 ||
                      vm.selectedApplicants.size > vm.remainingCapacity
                    }
                    dataTest={`applicant-manager-bulk-actions-invite-${vm.workspacePosting?.id}`}
                  >
                    Invite selected
                  </DropdownItem>
                  <DropdownItem
                    onClick={vm.rescindInvitationForSelected}
                    disabled={vm.selectedApplicants.size < 1}
                    dataTest={`applicant-manager-bulk-actions-rescind-invitation-${vm.workspacePosting?.id}`}
                  >
                    Rescind selected
                  </DropdownItem>
                  <DropdownItem
                    onClick={vm.exportApplicantsAsCSV}
                    icon={<Icon name="download" size={24} />}
                    dataTest={`applicant-manager-export-csv-${vm.workspacePosting?.id}`}
                  >
                    Export CSV
                  </DropdownItem>
                </div>
              </Dropdown>
            </TableSearchbar>
            <TableScrollingContainer>{children}</TableScrollingContainer>
          </TableHost>
        </ContentLayout>
      </div>
    )
  },
)
